import type {
  PayoutCreateRequest,
  PayoutResponse,
  PayoutUpdateRequest,
  PayoutListParams,
  PayoutListResponse,
} from '../types';
import type { RequestFn } from '../client';

/** Create and manage payouts — fund transfers from merchant to a recipient bank account. */
export class Payouts {
  constructor(private readonly request: RequestFn) {}

  /**
   * Create a new payout.
   *
   * @param params - Payout parameters including amount, currency, and destination.
   * @returns The created payout.
   *
   * @example
   * ```typescript
   * const payout = await delopay.payouts.create({
   *   amount: 10000,
   *   currency: 'EUR',
   *   customer_id: 'cus_123',
   * });
   * ```
   */
  async create(params: PayoutCreateRequest): Promise<PayoutResponse> {
    return this.request('POST', '/payouts/create', { body: params });
  }

  /**
   * Retrieve a payout by its ID.
   *
   * @param payoutId - The unique payout ID.
   * @returns The payout.
   */
  async retrieve(payoutId: string): Promise<PayoutResponse> {
    return this.request('GET', `/payouts/${encodeURIComponent(payoutId)}`);
  }

  /**
   * Update a payout before it is confirmed.
   *
   * @param payoutId - The payout ID to update.
   * @param params - Fields to update.
   * @returns The updated payout.
   */
  async update(payoutId: string, params: PayoutUpdateRequest): Promise<PayoutResponse> {
    return this.request('PUT', `/payouts/${encodeURIComponent(payoutId)}`, { body: params });
  }

  /**
   * Confirm a payout, triggering the actual transfer.
   *
   * @param payoutId - The payout ID to confirm.
   * @param params - Optional confirmation parameters.
   * @returns The updated payout.
   */
  async confirm(payoutId: string, params?: PayoutUpdateRequest): Promise<PayoutResponse> {
    return this.request('POST', `/payouts/${encodeURIComponent(payoutId)}/confirm`, {
      body: params,
    });
  }

  /**
   * Cancel a payout before it is fulfilled.
   *
   * @param payoutId - The payout ID to cancel.
   * @returns The cancelled payout.
   */
  async cancel(payoutId: string): Promise<PayoutResponse> {
    return this.request('POST', `/payouts/${encodeURIComponent(payoutId)}/cancel`);
  }

  /**
   * Mark a payout as fulfilled (manual confirmation of successful transfer).
   *
   * @param payoutId - The payout ID to fulfil.
   * @returns The fulfilled payout.
   */
  async fulfill(payoutId: string): Promise<PayoutResponse> {
    return this.request('POST', `/payouts/${encodeURIComponent(payoutId)}/fulfill`);
  }

  /**
   * List payouts, optionally filtered by status or date range.
   *
   * @param params - Optional filter and pagination parameters.
   * @returns Paginated list of payouts.
   */
  async list(params?: PayoutListParams): Promise<PayoutListResponse> {
    return this.request('GET', '/payouts/list', {
      query: params as Record<string, string | number | undefined>,
    });
  }

  // --- OLAP extensions (Task 4.5) ---

  /** List payouts (profile-scoped). `GET /payouts/profile/list` */
  async listByProfile(params?: PayoutListParams): Promise<PayoutListResponse> {
    return this.request('GET', '/payouts/profile/list', {
      query: params as Record<string, string | number | undefined>,
    });
  }

  /** List payouts by filter (POST body). `POST /payouts/list` */
  async listByFilter(params: Record<string, unknown>): Promise<PayoutListResponse> {
    return this.request('POST', '/payouts/list', { body: params });
  }

  /** Get payout filter options. `GET /payouts/filter` */
  async getFilters(
    params?: Record<string, string | number | undefined>,
  ): Promise<Record<string, unknown>> {
    return this.request('GET', '/payouts/filter', { query: params });
  }

  /** Get payout filters (profile-scoped). `GET /payouts/profile/filter` */
  async getFiltersByProfile(
    params?: Record<string, string | number | undefined>,
  ): Promise<Record<string, unknown>> {
    return this.request('GET', '/payouts/profile/filter', { query: params });
  }

  /** Get payout aggregates. `GET /payouts/aggregate` */
  async aggregate(
    params?: Record<string, string | number | undefined>,
  ): Promise<Record<string, unknown>> {
    return this.request('GET', '/payouts/aggregate', { query: params });
  }

  /** Get payout aggregates (profile-scoped). `GET /payouts/profile/aggregate` */
  async aggregateByProfile(
    params?: Record<string, string | number | undefined>,
  ): Promise<Record<string, unknown>> {
    return this.request('GET', '/payouts/profile/aggregate', { query: params });
  }

  /** Manually update payout status. `PUT /payouts/{payoutId}/manual-update` */
  async manualUpdate(payoutId: string, params: Record<string, unknown>): Promise<PayoutResponse> {
    return this.request('PUT', `/payouts/${encodeURIComponent(payoutId)}/manual-update`, {
      body: params,
    });
  }
}
